import React, { useEffect, useState, useRef } from 'react';
import './App.css';
import { parseItemCodes, fetchItemImages, downloadImage } from './apiUtils';
import config from './config';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

function LanguageButtons({ current, onChange }) {
  return (
    <div className="lang-btns">
      <button
        className={`lang-btn${current === 'no' ? ' active' : ''}`}
        onClick={() => onChange('no')}
        type="button"
      >
        Norsk
      </button>
      <button
        className={`lang-btn${current === 'en' ? ' active' : ''}`}
        onClick={() => onChange('en')}
        type="button"
      >
        English
      </button>
    </div>
  );
}

function Carousel({ images, onDownloadImage, onDownloadAllForItem, onPreview, lang, itemCode }) {
  const [index, setIndex] = useState(0);
  if (!images.length) return null;
  const prev = () => setIndex(i => (i === 0 ? images.length - 1 : i - 1));
  const next = () => setIndex(i => (i === images.length - 1 ? 0 : i + 1));
  const current = images[index];
  // Calculate number of rows needed for thumbs (5 per row)
  const thumbRows = Math.ceil(images.length / 5);
  // Increase minHeight for each extra row (base: 420px, +40px per extra row)
  const minHeight = 420 + (thumbRows > 1 ? (thumbRows - 1) * 40 : 0);
  return (
    <div className="carousel-card" style={{ width: '100%', minHeight, maxWidth: 320, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', position: 'relative' }}>
      <h2 className="carousel-title" style={{ margin: 0, padding: 0, fontSize: '1.1rem', fontWeight: 600 }}>{itemCode}</h2>
      <div className="carousel-box" style={{ width: '100%', position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ position: 'relative', width: '100%', height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <button className="carousel-arrow over-image" onClick={prev} aria-label="Previous image" style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 2 }}>&#8592;</button>
          <img
            src={current.Permalink}
            alt={current.Title || ''}
            className="carousel-img main"
            onClick={() => onPreview(current)}
            style={{ cursor: 'zoom-in', width: '98%', height: 250, objectFit: 'cover', borderRadius: 12, boxShadow: '0 2px 16px rgba(30,40,90,0.13)', display: 'block', margin: '0 auto' }}
          />
          <button className="carousel-arrow over-image" onClick={next} aria-label="Next image" style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 2 }}>&#8594;</button>
        </div>
        <div className="carousel-thumbs-row" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 36px)',
          gap: 8,
          justifyContent: 'center',
          alignItems: 'center',
          margin: '10px auto 0 auto',
          maxWidth: '100%'
        }}>
          {images.map((img, i) => (
            <img
              key={img.DocumentId}
              src={img.Permalink}
              alt={img.Title || ''}
              className={`carousel-thumb${i === index ? ' active' : ''}`}
              style={{
                width: 36,
                height: 36,
                objectFit: 'cover',
                border: i === index ? '2px solid #4f8cff' : '2px solid #e0e6ed',
                borderRadius: 8,
                boxShadow: i === index ? '0 2px 8px #4f8cff33' : '0 1px 4px rgba(30,40,90,0.08)',
                cursor: 'pointer',
                opacity: i === index ? 1 : 0.7,
                transition: 'border 0.2s, box-shadow 0.2s, opacity 0.2s',
                margin: 0
              }}
              onClick={() => setIndex(i)}
            />
          ))}
        </div>
      </div>
      <div className="gallery-actions static-bottom" style={{ position: 'absolute', left: 0, right: 0, bottom: 5, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 3 }}>
        <button className="primary-btn download-selected" style={{ marginTop: 0 }} onClick={() => onDownloadImage(current.DocumentId, current.Format, current)}>
          {lang === 'no' ? 'Last ned valgt bilde' : 'Download Selected Image'}
        </button>
        <button onClick={() => onDownloadAllForItem(images)} className="primary-btn download-all" style={{ marginTop: 8 }} disabled={!images.length}>
          {lang === 'no' ? 'Last ned alle bilder' : 'Download All Images'}
        </button>
      </div>
    </div>
  );
}

function ImageModal({ image, onClose }) {
  if (!image) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <img src={image.Permalink} alt={image.Title || ''} className="modal-img" />
        <button className="primary-btn" onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

function AdminLoginModal({ open, onClose, onLogin, error }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const inputRef = useRef();
  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);
  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ minWidth: 320 }}>
        <h2>Admin Login</h2>
        <form onSubmit={e => { e.preventDefault(); onLogin(username, password); }}>
          <input ref={inputRef} type="text" placeholder="Username" value={username} onChange={e => setUsername(e.target.value)} autoComplete="username" />
          <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" />
          <button className="primary-btn" type="submit">Login</button>
        </form>
        {error && <div className="error" style={{ marginTop: 8 }}>{error}</div>}
        <button className="primary-btn" style={{ marginTop: 12 }} onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

function AdminDashboardModal({ open, onClose, logs }) {
  const [tab, setTab] = useState('api');
  const [downloadStats, setDownloadStats] = useState({ today: 0, week: 0, month: 0 });
  const [deleting, setDeleting] = useState(false);
  const [apiTestOpen, setApiTestOpen] = useState(false);
  const [apiTestResults, setApiTestResults] = useState([]);
  const [apiTesting, setApiTesting] = useState(false);

  // Helper to delete a single log entry
  const handleDeleteLog = async (type, index) => {
    if (!window.confirm('Are you sure you want to delete this log entry?')) return;
    setDeleting(true);
    await fetch(`/api/admin/logs/delete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}`
      },
      body: JSON.stringify({ type, index })
    });
    setDeleting(false);
    window.location.reload(); // Simple reload to refresh logs
  };

  // Helper to delete all logs of a type
  const handleDeleteAllLogs = async (type) => {
    if (!window.confirm('Are you sure you want to delete ALL logs of this type?')) return;
    setDeleting(true);
    await fetch(`/api/admin/logs/delete-all`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}`
      },
      body: JSON.stringify({ type })
    });
    setDeleting(false);
    window.location.reload();
  };

  useEffect(() => {
    if (open) {
      const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      if (!token) return;
      fetch('/api/admin/download-stats', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(r => r.json())
        .then(setDownloadStats)
        .catch(() => setDownloadStats({ today: 0, week: 0, month: 0 }));
    }
  }, [open]);

  if (!open) return null;

  // Helper for log list rendering
  const renderLogList = (type, items, columns, getRow) => (
    <div style={{
      maxHeight: 350,
      overflowY: 'auto',
      border: '1px solid #e0e6ed',
      borderRadius: 8,
      background: '#fafbfc',
      marginBottom: 12,
      boxShadow: '0 1px 6px #1a223610',
      padding: 0
    }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.98em' }}>
        <thead>
          <tr style={{ background: '#f0f4fa', position: 'sticky', top: 0 }}>
            {columns.map((col, i) => <th key={i} style={{ padding: '8px 4px', fontWeight: 600, borderBottom: '1px solid #e0e6ed', textAlign: 'left' }}>{col}</th>)}
            <th style={{ width: 44 }}></th>
          </tr>
        </thead>
        <tbody>
          {items && items.length ? items.map((log, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #f0f4fa', background: i % 2 ? '#fff' : '#f7f9fb' }}>
              {getRow(log, i)}
              <td>
                <button
                  className="secondary-btn"
                  style={{ padding: '0.2rem 0.7rem', fontSize: '0.95em', margin: 0 }}
                  disabled={deleting}
                  onClick={() => handleDeleteLog(type, i)}
                  title="Delete this log"
                >🗑️</button>
              </td>
            </tr>
          )) : (
            <tr><td colSpan={columns.length + 1} style={{ textAlign: 'center', color: '#888', padding: 16 }}>No logs.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );

  // Helper for grouped log list rendering
  const renderGroupedLogList = (type, groupedItems, columns, getRow) => (
    <div style={{
      maxHeight: 350,
      overflowY: 'auto',
      border: '1px solid #e0e6ed',
      borderRadius: 8,
      background: '#fafbfc',
      marginBottom: 12,
      boxShadow: '0 1px 6px #1a223610',
      padding: 0
    }}>
      {Object.keys(groupedItems).sort((a, b) => b.localeCompare(a)).map(date => (
        <div key={date}>
          <div style={{ background: '#e0e6ed', fontWeight: 600, padding: '6px 12px', fontSize: '1em' }}>{date}</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.98em' }}>
            <thead>
              <tr style={{ background: '#f0f4fa', position: 'sticky', top: 0 }}>
                {columns.map((col, i) => <th key={i} style={{ padding: '8px 4px', fontWeight: 600, borderBottom: '1px solid #e0e6ed', textAlign: 'left' }}>{col}</th>)}
                <th style={{ width: 44 }}></th>
              </tr>
            </thead>
            <tbody>
              {groupedItems[date] && groupedItems[date].length ? groupedItems[date].map((log, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f0f4fa', background: i % 2 ? '#fff' : '#f7f9fb' }}>
                  {getRow(log, i)}
                  <td>
                    <button
                      className="secondary-btn"
                      style={{ padding: '0.2rem 0.7rem', fontSize: '0.95em', margin: 0 }}
                      disabled={deleting}
                      onClick={() => handleDeleteLog(type, i)}
                      title="Delete this log"
                    >🗑️</button>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={columns.length + 1} style={{ textAlign: 'center', color: '#888', padding: 16 }}>No logs.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );

  // List of API endpoints to test (add more as needed)
  const apiEndpoints = [
    { endpoint: '/api/log/api', method: 'POST', body: { endpoint: '/test', method: 'GET', status: 200, time: new Date().toISOString() } },
    { endpoint: '/api/log/visit', method: 'POST', body: { path: '/test', time: new Date().toISOString() } },
    { endpoint: '/api/log/download', method: 'POST', body: { itemCode: 'TEST', filename: 'test.jpg', time: new Date().toISOString(), isBatch: false } },
    { endpoint: '/api/admin/logs', method: 'GET' },
    { endpoint: '/api/admin/download-stats', method: 'GET' },
    // Imageshop API endpoints (using config)
    { endpoint: `${config.API_BASE}/DocumentInfo/GetPermalinksFromDocumentInfoValueByBaseId?documentBaseId=${config.DOCUMENT_BASE_ID}&language=no&value=TEST`, method: 'GET', headers: { 'Authorization': `Bearer ${config.TOKEN}` } },
    // Direct to imageshop.no
    { endpoint: `https://api.imageshop.no/DocumentInfo/GetPermalinksFromDocumentInfoValueByBaseId?documentBaseId=${config.DOCUMENT_BASE_ID}&language=no&value=TEST`, method: 'GET', headers: { 'token': config.TOKEN } },
    { endpoint: `https://api.imageshop.no/Download`, method: 'POST', headers: { 'Content-Type': 'application/xml', 'Accept': 'application/json', 'token': config.TOKEN }, body: `<?xml version="1.0"?>\n<DownloadRequest>\n  <DocumentId>4061915</DocumentId>\n  <Quality>FullSize</Quality>\n  <DownloadAsAttachment>true</DownloadAsAttachment>\n</DownloadRequest>` },
    // Add more endpoints as needed
  ];

  // Function to test all APIs
  const handleTestApis = async () => {
    setApiTesting(true);
    const results = [];
    for (const api of apiEndpoints) {
      try {
        const fetchOptions = {
          method: api.method,
          headers: { ...(api.headers || {}), ...(api.endpoint.startsWith('/api/admin') ? { 'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}` } : {}) },
        };
        if (api.method !== 'GET' && api.body) {
          // If body is a string, send as-is (for XML), else JSON
          fetchOptions.body = typeof api.body === 'string' ? api.body : JSON.stringify(api.body);
          if (typeof api.body !== 'string' && !fetchOptions.headers['Content-Type']) {
            fetchOptions.headers['Content-Type'] = 'application/json';
          }
        }
        // For GET requests, remove Content-Type if present
        if (api.method === 'GET' && fetchOptions.headers['Content-Type']) {
          delete fetchOptions.headers['Content-Type'];
        }
        const res = await fetch(api.endpoint, fetchOptions);
        let message = '';
        try { message = await res.text(); } catch {}
        results.push({ endpoint: api.endpoint, status: res.status, message: res.status === 200 ? 'OK' : message });
      } catch (e) {
        results.push({ endpoint: api.endpoint, status: 'ERR', message: e.message });
      }
    }
    setApiTestResults(results);
    setApiTestOpen(true);
    setApiTesting(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ minWidth: 700, maxWidth: 900, maxHeight: '90vh', overflowY: 'auto', position: 'relative', paddingBottom: 60 }}>
        <h2 style={{ marginBottom: 8 }}>Admin Dashboard</h2>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <button className={tab === 'api' ? 'tab-btn active' : 'tab-btn'} onClick={() => setTab('api')}>API Calls</button>
          <button className={tab === 'visits' ? 'tab-btn active' : 'tab-btn'} onClick={() => setTab('visits')}>Visits</button>
          <button className={tab === 'downloads' ? 'tab-btn active' : 'tab-btn'} onClick={() => setTab('downloads')}>Downloads</button>
          <button className={tab === 'sessions' ? 'tab-btn active' : 'tab-btn'} onClick={() => setTab('sessions')}>Download Sessions</button>
        </div>
        {tab === 'api' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <h3 style={{ margin: 0 }}>API Calls</h3>
              <div>
                <button className="secondary-btn" style={{ padding: '0.3rem 1.2rem', fontSize: '0.95em', marginRight: 8 }} disabled={apiTesting} onClick={handleTestApis}>
                  {apiTesting ? 'Testing...' : 'Test All APIs'}
                </button>
                <button className="secondary-btn" style={{ padding: '0.3rem 1.2rem', fontSize: '0.95em' }} disabled={deleting} onClick={() => handleDeleteAllLogs('api')}>Delete All</button>
              </div>
            </div>
            <ApiTestModal open={apiTestOpen} onClose={() => setApiTestOpen(false)} results={apiTestResults} />
            {renderGroupedLogList(
              'api',
              logs.api || {},
              ['Time', 'Method', 'Endpoint', 'Status'],
              (log) => <>
                <td>{log.time}</td>
                <td>{log.method}</td>
                <td style={{ wordBreak: 'break-all' }}>{log.endpoint}</td>
                <td>{log.status || ''}</td>
              </>
            )}
          </>
        )}
        {tab === 'visits' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <h3 style={{ margin: 0 }}>Page Visits</h3>
              <button className="secondary-btn" style={{ padding: '0.3rem 1.2rem', fontSize: '0.95em' }} disabled={deleting} onClick={() => handleDeleteAllLogs('visits')}>Delete All</button>
            </div>
            {renderGroupedLogList(
              'visits',
              logs.visits || {},
              ['Time', 'Path', 'IP'],
              (log) => <>
                <td>{log.time}</td>
                <td style={{ wordBreak: 'break-all' }}>{log.path}</td>
                <td>{log.ip || ''}</td>
              </>
            )}
          </>
        )}
        {tab === 'downloads' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <h3 style={{ margin: 0 }}>Image Downloads</h3>
              <div>
                <span style={{ marginRight: 12, fontWeight: 500 }}>
                  <span style={{ color: '#4f8cff' }}>Today:</span> {downloadStats.today} &nbsp;|&nbsp;
                  <span style={{ color: '#4f8cff' }}>Week:</span> {downloadStats.week} &nbsp;|&nbsp;
                  <span style={{ color: '#4f8cff' }}>Month:</span> {downloadStats.month}
                </span>
                <button className="secondary-btn" style={{ padding: '0.3rem 1.2rem', fontSize: '0.95em' }} disabled={deleting} onClick={() => handleDeleteAllLogs('downloads')}>Delete All</button>
              </div>
            </div>
            {renderGroupedLogList(
              'downloads',
              logs.downloads || {},
              ['Time', 'Item Code', 'Filename', 'Batch', 'Elapsed (s)'],
              (log) => <>
                <td>{log.time}</td>
                <td>{log.itemCode}</td>
                <td style={{ wordBreak: 'break-all' }}>{log.filename}</td>
                <td>{log.isBatch ? 'Yes' : ''}</td>
                <td>{typeof log.elapsedMs === 'number' ? (log.elapsedMs / 1000).toFixed(2) : ''}</td>
              </>
            )}
          </>
        )}
        {tab === 'sessions' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <h3 style={{ margin: 0 }}>Download Sessions</h3>
              <button className="secondary-btn" style={{ padding: '0.3rem 1.2rem', fontSize: '0.95em' }} disabled={deleting} onClick={() => handleDeleteAllLogs('downloadSessions')}>Delete All</button>
            </div>
            <div style={{
              maxHeight: 350,
              overflowY: 'auto',
              border: '1px solid #e0e6ed',
              borderRadius: 8,
              background: '#fafbfc',
              marginBottom: 12,
              boxShadow: '0 1px 6px #1a223610',
              padding: 0
            }}>
              {logs.downloadSessions && Object.keys(logs.downloadSessions).length ?
                Object.entries(logs.downloadSessions).map(([sid, arr], i) => (
                  <details key={sid} style={{ margin: '0 0 8px 0', padding: '0 8px' }}>
                    <summary style={{ fontWeight: 600, cursor: 'pointer', padding: '6px 0' }}>
                      Session {i + 1} ({arr.length} downloads, first: {arr[0]?.time}, last: {arr[arr.length-1]?.time})
                    </summary>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.97em', marginBottom: 8 }}>
                      <thead>
                        <tr style={{ background: '#f0f4fa' }}>
                          <th style={{ padding: '4px 2px', fontWeight: 600 }}>Time</th>
                          <th style={{ padding: '4px 2px', fontWeight: 600 }}>Item Code</th>
                          <th style={{ padding: '4px 2px', fontWeight: 600 }}>Filename</th>
                          <th style={{ padding: '4px 2px', fontWeight: 600 }}>Batch</th>
                          <th style={{ width: 44 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {arr.map((log, j) => (
                          <tr key={j} style={{ borderBottom: '1px solid #f0f4fa', background: j % 2 ? '#fff' : '#f7f9fb' }}>
                            <td>{log.time}</td>
                            <td>{log.itemCode}</td>
                            <td style={{ wordBreak: 'break-all' }}>{log.filename}</td>
                            <td>{log.isBatch ? 'Yes' : ''}</td>
                            <td>
                              <button
                                className="secondary-btn"
                                style={{ padding: '0.2rem 0.7rem', fontSize: '0.95em', margin: 0 }}
                                disabled={deleting}
                                onClick={() => handleDeleteLog('downloadSessions', { sessionId: sid, index: j })}
                                title="Delete this log"
                              >🗑️</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </details>
                )) : <div style={{ textAlign: 'center', color: '#888', padding: 16 }}>No download sessions.</div>}
            </div>
          </>
        )}
        <button className="primary-btn" style={{ position: 'absolute', bottom: 16, left: 24 }} onClick={onClose}>Close</button>
        <button className="primary-btn" style={{ position: 'absolute', bottom: 16, right: 24 }} onClick={() => {
          fetch('/api/admin/logs/export', {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}` }
          })
            .then(res => {
              if (!res.ok) throw new Error('Failed to export logs');
              return res.blob();
            })
            .then(blob => {
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'logs.xlsx';
              a.click();
              window.URL.revokeObjectURL(url);
            })
            .catch(() => alert('Failed to export logs.'));
        }}>Export to Excel</button>
        {apiTestOpen && (
          <div className="api-test-results" style={{ marginTop: 16, padding: 16, borderTop: '1px solid #e0e6ed' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '1.1em' }}>API Test Results</h3>
            <button className="secondary-btn" style={{ marginBottom: 12 }} onClick={handleTestApis} disabled={apiTesting}>
              {apiTesting ? 'Testing...' : 'Re-test All APIs'}
            </button>
            <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #e0e6ed', borderRadius: 8, background: '#fafbfc', padding: 0 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.98em' }}>
                <thead>
                  <tr style={{ background: '#f0f4fa' }}>
                    <th style={{ padding: '8px 4px', fontWeight: 600, textAlign: 'left' }}>Endpoint</th>
                    <th style={{ padding: '8px 4px', fontWeight: 600, textAlign: 'left' }}>Status</th>
                    <th style={{ padding: '8px 4px', fontWeight: 600, textAlign: 'left' }}>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {apiTestResults.map((r, i) => (
                    <tr key={i} style={{ background: r.status === 200 ? '#e6fae6' : '#fae6e6' }}>
                      <td style={{ padding: '6px 4px', wordBreak: 'break-all' }}>{r.endpoint}</td>
                      <td style={{ padding: '6px 4px' }}>{r.status}</td>
                      <td style={{ padding: '6px 4px' }}>{r.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ApiTestModal({ open, onClose, results }) {
  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ minWidth: 400, maxWidth: 600 }}>
        <h3>API Test Results</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.98em', marginBottom: 12 }}>
          <thead>
            <tr style={{ background: '#f0f4fa' }}>
              <th style={{ padding: '8px 4px', fontWeight: 600, textAlign: 'left' }}>Endpoint</th>
              <th style={{ padding: '8px 4px', fontWeight: 600, textAlign: 'left' }}>Status</th>
              <th style={{ padding: '8px 4px', fontWeight: 600, textAlign: 'left' }}>Message</th>
            </tr>
          </thead>
          <tbody>
            {results.map((r, i) => (
              <tr key={i} style={{ background: r.status === 200 ? '#e6fae6' : '#fae6e6' }}>
                <td style={{ padding: '6px 4px', wordBreak: 'break-all' }}>{r.endpoint}</td>
                <td style={{ padding: '6px 4px' }}>{r.status}</td>
                <td style={{ padding: '6px 4px' }}>{r.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <button className="primary-btn" onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

// Helper: log download (now supports elapsedMs)
// Only declare this ONCE, outside of any function/component!
const logDownload = (itemCode, filename, isBatch = false, elapsedMs) => {
  fetch('/api/log/download', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ itemCode, filename, time: new Date().toISOString(), isBatch, elapsedMs })
  });
};

// Helper: Run async tasks with concurrency limit and progress callback
async function promisePool(tasks, concurrency, onProgress) {
  let i = 0;
  let done = 0;
  const results = [];
  const run = async () => {
    while (i < tasks.length) {
      const cur = i++;
      try {
        results[cur] = await tasks[cur]();
      } catch (e) {
        results[cur] = undefined;
      }
      done++;
      if (onProgress) onProgress(done);
    }
  };
  const runners = Array(Math.min(concurrency, tasks.length)).fill(0).map(run);
  await Promise.all(runners);
  return results;
}

function App() {
  const [itemCodes, setItemCodes] = useState([]);
  const [itemImages, setItemImages] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [modalImage, setModalImage] = useState(null);
  const [lang, setLang] = useState('no');
  const [downloadProgress, setDownloadProgress] = useState(null);
  const [adminLoginOpen, setAdminLoginOpen] = useState(false);
  const [adminDashboardOpen, setAdminDashboardOpen] = useState(false);
  const [adminError, setAdminError] = useState('');
  const [adminToken, setAdminToken] = useState(null);
  const [adminLogs, setAdminLogs] = useState({ api: [], visits: [], downloads: [] });
  const [itemDownloadProgress, setItemDownloadProgress] = useState(null);
  // Track start time for download all images (all items)
  const [downloadAllStart, setDownloadAllStart] = useState(null);

  useEffect(() => {
    // Use config.ROUTE_PATTERN for parsing item codes
    const codes = parseItemCodes(window.location.pathname, config.ROUTE_PATTERN);
    setItemCodes(codes);
    setSearchInput(codes.join(','));
    setSearch(codes.join(','));
  }, []);

  useEffect(() => {
    async function fetchAll() {
      setLoading(true);
      setError('');
      const codes = search.split(',').map(c => c.trim()).filter(Boolean);
      if (!codes.length) {
        setItemImages({});
        setLoading(false);
        return;
      }
      const imagesByCode = {};
      let anyError = false;
      for (const code of codes) {
        try {
          // Pass documentBaseId from config
          const images = await fetchItemImages(code, config.API_BASE, config.TOKEN, config.DOCUMENT_BASE_ID);
          imagesByCode[code] = images;
        } catch (e) {
          imagesByCode[code] = [];
          anyError = true;
        }
      }
      setItemCodes(codes);
      setItemImages(imagesByCode);
      setLoading(false);
      if (anyError) {
        setError('Some items could not be loaded. Please check item codes or try again.');
      }
    }
    fetchAll();
  }, [search]);

  // Keyboard shortcut: Ctrl+Shift+L
  useEffect(() => {
    const handler = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'l') {
        setAdminLoginOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Log page visit on mount (only once per session)
  useEffect(() => {
    if (!sessionStorage.getItem('visit-logged')) {
      fetch('/api/log/visit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: window.location.pathname, time: new Date().toISOString() })
      });
      sessionStorage.setItem('visit-logged', '1');
    }
  }, []);

  // Helper: log API call
  const logApiCall = (endpoint, method, status) => {
    fetch('/api/log/api', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint, method, status, time: new Date().toISOString() })
    });
  };

  // Admin login
  const handleAdminLogin = async (username, password) => {
    setAdminError('');
    const res = await fetch('/api/admin/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });
    if (res.ok) {
      const data = await res.json();
      setAdminToken(data.token);
      localStorage.setItem('adminToken', data.token); // Store for dashboard stats
      setAdminLoginOpen(false);
      setAdminDashboardOpen(true);
      // Fetch logs
      fetch('/api/admin/logs', {
        headers: { 'Authorization': `Bearer ${data.token}` }
      })
        .then(r => r.json())
        .then(logs => setAdminLogs(logs));
    } else {
      setAdminError('Login failed.');
    }
  };

  // When dashboard is opened, refresh logs
  useEffect(() => {
    if (adminDashboardOpen && adminToken) {
      fetch('/api/admin/logs', {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      })
        .then(r => r.json())
        .then(logs => setAdminLogs(logs));
    }
  }, [adminDashboardOpen, adminToken]);

  const handleDownloadImage = async (documentId, format = 'jpg', imageObj) => {
    try {
      // Always use the itemCode from the imageObj or fallback to documentId
      let itemCode = documentId;
      if (imageObj) {
        // Use the itemCode prop from the Carousel (which is always correct)
        itemCode = imageObj.itemCode || imageObj.ItemCode || imageObj.value || imageObj.itemcode || imageObj.code || documentId;
      }
      const result = await downloadImage(documentId, config.TOKEN, true, config.DOWNLOAD_API, format);
      const ext = result.format || 'jpg';
      const url = window.URL.createObjectURL(result.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${itemCode}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
      // Log download
      logDownload(itemCode, `${itemCode}.${ext}`, false);
    } catch (e) {
      setError('Download failed. Please try again.');
    }
  };

  // Show progress bar when downloading all images for a single item
  const handleDownloadAllForItem = async (images) => {
    if (!images.length) return;
    setLoading(true);
    setItemDownloadProgress({ current: 0, total: images.length, percent: 0 });
    const zip = new JSZip();
    const itemCode = images[0].itemCode || images[0].ItemCode || images[0].value || images[0].itemcode || images[0].code || images[0].DocumentId;
    const start = Date.now(); // <-- Start time here
    // Prepare download tasks
    const tasks = images.map((img, i) => async () => {
      try {
        const result = await downloadImage(img.DocumentId, config.TOKEN, true, config.DOWNLOAD_API, img.Format);
        let filename = itemCode;
        if (i > 0) filename = `${itemCode}_${i}`;
        const ext = result.format || 'jpg';
        zip.folder(itemCode).file(`${filename}.${ext}`, result.blob);
      } catch (e) {
        // skip failed
      }
    });
    let processed = 0;
    await promisePool(tasks, 8, (done) => {
      processed = done;
      setItemDownloadProgress({
        current: processed,
        total: images.length,
        percent: Math.round((processed / images.length) * 100)
      });
    });
    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, `${itemCode}.zip`);
    const elapsedMs = Date.now() - start; // <-- End time here
    // Log only one download for the batch, with elapsed time
    logDownload(itemCode, `${itemCode}.zip`, true, elapsedMs);
    setLoading(false);
    setItemDownloadProgress(null);
  };

  // Download all images for all items, with elapsed time
  const handleDownloadAll = async () => {
    setLoading(true);
    setDownloadProgress({ current: 0, total: 0, percent: 0 });
    setDownloadAllStart(Date.now()); // <-- Start time here
    const zip = new JSZip();
    let totalImages = 0;
    for (const code of itemCodes) {
      totalImages += (itemImages[code] || []).length;
    }
    // Prepare all download tasks as functions
    const allTasks = [];
    const fileMeta = [];
    for (const code of itemCodes) {
      const images = itemImages[code] || [];
      for (let i = 0; i < images.length; i++) {
        const img = images[i];
        allTasks.push(async () => {
          try {
            const result = await downloadImage(img.DocumentId, config.TOKEN, true, config.DOWNLOAD_API, img.Format);
            let filename = code;
            if (i > 0) filename = `${code}_${i}`;
            const ext = result.format || 'jpg';
            zip.folder(code).file(`${filename}.${ext}`, result.blob);
          } catch (e) {
            // skip failed
          }
        });
        fileMeta.push({ code, i });
      }
    }
    let processed = 0;
    await promisePool(allTasks, 12, (done) => {
      processed = done;
      setDownloadProgress({
        current: processed,
        total: totalImages,
        percent: Math.round((processed / totalImages) * 100)
      });
    });
    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, 'HomebrandsImages.zip');
    const elapsedMs = downloadAllStart ? Date.now() - downloadAllStart : undefined; // <-- End time here
    // Log only one download for the batch, with elapsed time
    logDownload('ALL', 'HomebrandsImages.zip', true, elapsedMs);
    setLoading(false);
    setDownloadProgress(null);
    setDownloadAllStart(null);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setError('');
  };

  return (
    <div className="App">
      <ImageModal image={modalImage} onClose={() => setModalImage(null)} />
      <AdminLoginModal open={adminLoginOpen} onClose={() => setAdminLoginOpen(false)} onLogin={handleAdminLogin} error={adminError} />
      <AdminDashboardModal open={adminDashboardOpen} onClose={() => {
        setAdminDashboardOpen(false);
        localStorage.removeItem('adminToken');
      }} logs={adminLogs} />
      <ApiTestModal open={false} onClose={() => {}} results={[]} />
      <header className="app-header minimal" style={{ padding: '0.4rem 0.5rem 0.3rem 0.5rem', marginBottom: '0.7rem', minHeight: 0 }}>
        <div className="header-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', maxWidth: 1200, margin: '0 auto', minHeight: 0 }}>
          <div className="logo-title" style={{ flex: '0 0 auto' }}>
            <img src={process.env.PUBLIC_URL + '/company_logo.png'} alt="Homebrands logo" className="logo-img" style={{ width: 120, height: 52, objectFit: 'contain' }} />
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            <LanguageButtons current={lang} onChange={setLang} />
          </div>
        </div>
        <h1 className="main-title" style={{ fontSize: '0.95rem', margin: '0.2rem 0 0.2rem 0', fontWeight: 500 }}>{lang === 'no' ? 'Bilder fra din ordre' : 'Images from your order'}</h1>
        <form className="search-bar" onSubmit={handleSearch} style={{ marginBottom: '0.3rem', position: 'relative' }}>
          <div className="search-input-wrapper">
            <input
              type="text"
              placeholder={lang === 'no' ? 'Skriv inn varenummer adskilt med komma' : 'Enter item codes, comma separated'}
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              aria-label={lang === 'no' ? 'Søk varenummer' : 'Search item codes'}
            />
            <button
              type="submit"
              className="search-icon-btn"
              aria-label={lang === 'no' ? 'Søk' : 'Search'}
              tabIndex={0}
            >
              <svg viewBox="0 0 20 20" fill="none">
                <circle cx="9" cy="9" r="7" stroke="currentColor" strokeWidth="2"/>
                <line x1="14.2" y1="14.2" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </form>
        <button onClick={handleDownloadAll} className="primary-btn download-all" disabled={loading || !Object.values(itemImages).some(arr => arr.length)} style={{ fontSize: '0.95rem', padding: '0.4rem 1rem', height: 28 }}>
          {lang === 'no' ? 'Last ned alle bilder' : 'Download all images'}
        </button>
      </header>
      {loading && <div className="loading">{lang === 'no' ? 'Laster bilder...' : 'Loading images...'}</div>}
      {error && <div className="error">{error}</div>}
      {downloadProgress && (
        <div className="download-progress" style={{ margin: '1rem auto', maxWidth: 400, textAlign: 'center' }}>
          <div style={{ marginBottom: 6 }}>
            {lang === 'no' ? 'Laster ned bilder:' : 'Downloading images:'} {downloadProgress.current} / {downloadProgress.total} ({downloadProgress.percent}%)
          </div>
          <div style={{ marginBottom: 6, color: '#888', fontSize: '0.97em' }}>
            {lang === 'no' ? 'Dette kan ta litt tid når du laster ned mange bilder.' : 'This might take some time when downloading multiple images.'}
          </div>
          <div style={{ background: '#e0e6ed', borderRadius: 8, height: 16, width: '100%', overflow: 'hidden' }}>
            <div style={{ background: '#4f8cff', height: '100%', width: `${downloadProgress.percent}%`, transition: 'width 0.2s', borderRadius: 8 }} />
          </div>
        </div>
      )}
      {itemDownloadProgress && (
        <div className="download-progress" style={{ margin: '1rem auto', maxWidth: 400, textAlign: 'center' }}>
          <div style={{ marginBottom: 6 }}>
            {lang === 'no'
              ? `Laster ned bilder for varenummer: ${itemDownloadProgress.current} / ${itemDownloadProgress.total} (${itemDownloadProgress.percent}%)`
              : `Downloading images for item: ${itemDownloadProgress.current} / ${itemDownloadProgress.total} (${itemDownloadProgress.percent}%)`}
          </div>
          <div style={{ background: '#e0e6ed', borderRadius: 8, height: 16, width: '100%', overflow: 'hidden' }}>
            <div style={{ background: '#4f8cff', height: '100%', width: `${itemDownloadProgress.percent}%`, transition: 'width 0.2s', borderRadius: 8 }} />
          </div>
        </div>
      )}
      <div className="gallery-grid">
        {itemCodes.map(code => {
          const images = itemImages[code] || [];
          if (!images.length) return null;
          return (
            <div className="gallery-item" key={code}>
              <Carousel images={images} onDownloadImage={handleDownloadImage} onDownloadAllForItem={handleDownloadAllForItem} onPreview={setModalImage} lang={lang} itemCode={code} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default App;
